import { useState, useEffect } from 'react';
import { requirementsApi } from '../services/api';
import { chatApi } from '../services/chatapi';
import { saveWithConflictPrompt } from '../utils/conflict';

const useAiFunctions = (selectedItemId, refreshTree, setError, setLoading, setSuccess) => {
  // AI Dialog state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiDialogTitle, setAiDialogTitle] = useState('');
  const [aiDialogContent, setAiDialogContent] = useState('');
  const [aiDialogType, setAiDialogType] = useState('');
  const [aiDialogLoading, setAiDialogLoading] = useState(false);
  
  // Entity dialog state
  const [entitiesDialogOpen, setEntitiesDialogOpen] = useState(false);
  const [entitiesData, setEntitiesData] = useState(null);
  
  // Review feedback dialog state
  const [reviewFeedbackDialogOpen, setReviewFeedbackDialogOpen] = useState(false);
  const [reviewFeedbackData, setReviewFeedbackData] = useState(null);
  const [originalContent, setOriginalContent] = useState('');
  
  // Decompose dialog state
  const [decomposeDialogOpen, setDecomposeDialogOpen] = useState(false);
  const [decomposeData, setDecomposeData] = useState(null);
  
  // Test case dialog state
  const [testCaseDialogOpen, setTestCaseDialogOpen] = useState(false);
  const [testCaseData, setTestCaseData] = useState(null);

  // Implementation Dialog state
  const [implementationDialogOpen, setImplementationDialogOpen] = useState(false);
  const [implementationData, setImplementationData] = useState(null);
  const [currentModel, setCurrentModel] = useState(() => {
    return localStorage.getItem('preferredAiModel') || 'gpt-4o';
  });
  // CONF2: the per-item AI tools must use the LIVE provider catalogue, not a
  // hard-coded gpt-4o/gpt-4o-mini/gpt-35-turbo list (which could offer a model
  // the active provider can't serve). Sourced from /api/chat/models, like the
  // chat panel (useAiModels / T1.24).
  const [availableModels, setAvailableModels] = useState([]);

  const updateAvailableModels = async () => {
    try {
      const response = await chatApi.getAvailableModels();
      const models = (response?.models || []).map(m => ({ id: m.id, name: m.name || m.id }));
      if (models.length) {
        setAvailableModels(models);
        // Reconcile the selected model with the live list, but never override an
        // explicit stored user preference (matches the chat-panel behaviour).
        const ids = models.map(m => m.id);
        setCurrentModel(prev => {
          if (localStorage.getItem('preferredAiModel')) return prev;
          if (prev && ids.includes(prev)) return prev;
          if (response.current && ids.includes(response.current)) return response.current;
          return ids[0];
        });
      }
    } catch (err) {
      // Live catalogue unavailable — keep the current selection; non-fatal.
      console.warn('Could not load AI models for per-item tools:', err.message);
    }
  };

  // Initialize available models when component loads
  useEffect(() => {
    updateAvailableModels();
  }, []);

  // Close handlers
  const handleCloseAiDialog = () => {
    setAiDialogOpen(false);
  };

  const handleCloseEntitiesDialog = () => {
    setEntitiesDialogOpen(false);
  };
  
  const handleCloseReviewFeedbackDialog = () => {
    setReviewFeedbackDialogOpen(false);
  };
  
  const handleCloseDecomposeDialog = () => {
    setDecomposeDialogOpen(false);
  };
  
  const handleCloseTestCaseDialog = () => {
    setTestCaseDialogOpen(false);
  };

  const handleCloseImplementationDialog = () => {
    setImplementationDialogOpen(false);
  };

  // Enhanced review function that uses the ReviewFeedbackDialog
  const handleReviewWithAI = async () => {
    if (!selectedItemId) return;
    
    // Set global loading state
    if (setLoading) setLoading(true);
    
    // Open the dialog first with loading state
    setReviewFeedbackDialogOpen(true);
    setReviewFeedbackData(null); // Set to null to indicate loading
    
    try {
      console.log(`Reviewing requirement ${selectedItemId} with AI`);
      
      // Get the item to review for saving the original content
      const item = await requirementsApi.getItem(selectedItemId);
      // Guard against a null/non-string description (matches the review-refine
      // path below); DiffViewer also coerces defensively (T1.5).
      setOriginalContent(item.description || '');
      
      // Call the API endpoint
      const result = await requirementsApi.reviewRequirement(selectedItemId);
      
      // Update the dialog with the feedback data
      setReviewFeedbackData(result.feedback);
      
    } catch (err) {
      if (setError) setError(`AI review failed: ${err.message}`);
      // Don't close the dialog, show error in it
      setReviewFeedbackData({
        message: `Error: ${err.message || 'Failed to review requirement'}`,
        recommendation: []
      });
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Handler for applying review recommendations
  const handleApplyRecommendations = async (editedRecommendation) => {
    if (!selectedItemId) return;
    
    // Set loading state
    if (setLoading) setLoading(true);
    
    try {
      // #572: PUT now requires a `version` precondition — fetch the item's
      // CURRENT version right before applying (not the one from the earlier
      // review fetch, which may be stale by the time the user applies it).
      const current = await requirementsApi.getItem(selectedItemId);
      // Update the requirement with edited recommendation.
      // #579: if someone saved the item between that GET and this PUT, the 409 used to
      // read "Failed to apply recommendations: Request failed with status code 409".
      // Offer reload / keep-mine instead — applying an AI rewrite over someone's fresh
      // manual edit is exactly the loss #572 set out to prevent, so it must be a choice.
      const res = await saveWithConflictPrompt(selectedItemId, {
        description: editedRecommendation,
        version: current.version
      }, { label: current.display_id || current.title || selectedItemId });

      // Show success message
      if (setError) {
        setError(res.conflict
          ? 'Not applied — that item had already been changed; reloaded the newer version.'
          : 'Recommendations applied successfully.', 'success');
      }
      
      // Close the dialog and refresh tree
      setReviewFeedbackDialogOpen(false);
      if (refreshTree) refreshTree();
    } catch (err) {
      if (setError) setError(`Failed to apply recommendations: ${err.message}`);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  const handleCheckConsistency = async () => {
    if (!selectedItemId) return;
    
    // Set global loading state
    if (setLoading) setLoading(true);
    
    // Open the dialog first with loading state
    setAiDialogOpen(true);
    setAiDialogTitle('Consistency Check Results');
    setAiDialogContent(null); // Set to null to show loading state
    setAiDialogLoading(true); // Explicitly set loading to true
    setAiDialogType('consistency');
    
    try {
      console.log(`Checking consistency for ${selectedItemId}`);
      
      // Call the API endpoint
      const result = await requirementsApi.checkConsistency(selectedItemId);
      
      // After receiving the data, just pass the data directly to the dialog
      setAiDialogContent(result);
      
    } catch (err) {
      if (setError) setError(`Consistency check failed: ${err.message}`);
      setAiDialogContent({
        consistency: `Error: ${err.message || 'Failed to check consistency'}`
      });
    } finally {
      // Set loading states to false when done
      if (setLoading) setLoading(false);
      setAiDialogLoading(false);
    }
  };

  // Enhanced decompose function that uses the DecomposeDialog
  const handleDecompose = async () => {
    if (!selectedItemId) return;
    
    // Set both global and dialog-specific loading states
    if (setLoading) setLoading(true);
    
    // Open the dialog first with loading state
    setDecomposeDialogOpen(true);
    setDecomposeData(null); // Set to null to show loading state
    
    try {
      console.log(`Decomposing ${selectedItemId}`);
      
      // Call the API endpoint
      const result = await requirementsApi.decomposeRequirement(selectedItemId);
      
      // Update dialog with results
      setDecomposeData(result);
      
    } catch (err) {
      if (setError) setError(`Decomposition failed: ${err.message}`);
      // Don't close the dialog, just show the error in it
      setDecomposeData({
        message: `Error: ${err.message || 'Failed to decompose requirement'}`,
        requirements: []
      });
    } finally {
      if (setLoading) setLoading(false);
    }
  };
  
  // Handler for creating decomposed requirements
  const handleCreateDecomposedRequirements = async (requirements, containerId) => {
    if (!selectedItemId || !requirements || !containerId) return;
    
    // Set loading state
    if (setLoading) setLoading(true);
    
    try {
      console.log(`Creating ${requirements.length} requirements under container ${containerId}`);
      
      // Create each requirement and link it to parent
      for (const requirement of requirements) {
        // Create the requirement
        const newReq = await requirementsApi.createItem({
          title: requirement.title,
          description: requirement.description,
          attributes: {
            type: 'requirement'
          },
          parent_id: containerId
        });
        
        // Create the link
        await requirementsApi.createLink(selectedItemId, newReq.id, 'decomposed_into');
      }
      
      // Refresh the tree
      if (refreshTree) refreshTree();
      
      // Show success message
      if (setError) setError(`Created ${requirements.length} requirements successfully.`, 'success');
      
    } catch (err) {
      if (setError) setError(`Failed to create requirements: ${err.message}`);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Enhanced test generation function that uses the TestCaseDialog
  const handleGenerateTests = async () => {
    if (!selectedItemId) return;
    
    // Set both global and dialog-specific loading states
    if (setLoading) setLoading(true);
    
    // Open the dialog first with loading state
    setTestCaseDialogOpen(true);
    setTestCaseData(null); // Set to null to show loading state
    
    try {
      console.log(`Generating tests for ${selectedItemId}`);
      
      // Call the API endpoint
      const result = await requirementsApi.generateTests(selectedItemId);
      
      // Update dialog with results
      setTestCaseData(result);
      
    } catch (err) {
      if (setError) setError(`Test generation failed: ${err.message}`);
      // Don't close the dialog, just show the error in it
      setTestCaseData({
        message: `Error: ${err.message || 'Failed to generate test cases'}`,
        tests: []
      });
    } finally {
      if (setLoading) setLoading(false);
    }
  };
  
  // Handler for creating test cases
  const handleCreateTestCases = async (testCases, containerId) => {
    if (!selectedItemId || !testCases || !containerId) return;
    
    // Set loading state
    if (setLoading) setLoading(true);
    
    try {
      console.log(`Creating ${testCases.length} test cases under container ${containerId}`);
      
      // Create each test case and link it to parent
      for (const testCase of testCases) {
        // Create the test case
        const newTest = await requirementsApi.createItem({
          title: testCase.title,
          description: testCase.description,
          attributes: {
            type: 'test_case'
          },
          parent_id: containerId
        });
        
        // Create the verification link
        await requirementsApi.createLink(newTest.id, selectedItemId, 'verifies');
      }
      
      // Refresh the tree
      if (refreshTree) refreshTree();
      
      // Show success message
      if (setError) setError(`Created ${testCases.length} test cases successfully.`, 'success');
      
    } catch (err) {
      if (setError) setError(`Failed to create test cases: ${err.message}`);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  const handleIdentifyEntities = async () => {
    if (!selectedItemId) return;
    
    // Set both global and dialog-specific loading states
    if (setLoading) setLoading(true);
    
    // Open the dialog first with loading state
    setEntitiesDialogOpen(true);
    setEntitiesData(null); // Set to null to show loading state
    
    try {
      console.log(`Identifying entities in ${selectedItemId}`);
      
      // Call the API endpoint
      const result = await requirementsApi.identifyEntities(selectedItemId);
      
      // Update dialog with the result data
      setEntitiesData(result);
      
    } catch (err) {
      if (setError) setError(`Entity identification failed: ${err.message}`);
      // Don't close the dialog, just show the error in it
      setEntitiesData({
        message: `Error: ${err.message || 'Failed to identify entities'}`,
        entities: []
      });
    } finally {
      if (setLoading) setLoading(false);
    }
  };
  
  // Handler for applying entities
  const handleApplyEntities = (selectedEntityIndices) => {
    if (!entitiesData || !entitiesData.entities || selectedEntityIndices.length === 0) return;
    
    // Get selected entities
    const entities = selectedEntityIndices.map(index => entitiesData.entities[index]);
    
    // Here we would implement the logic to apply these entities to the system
    // This could involve creating entity items, updating metadata, etc.
    console.log("Applying entities:", entities);
    
    // For now, we'll just show a success message
    if (setError) setError(`Applied ${entities.length} entities successfully.`, 'success');
    
    // Close the dialog
    handleCloseEntitiesDialog();
  };

  // Handle implement requirement request
  const handleImplement = async () => {
    if (!selectedItemId) return;
    
    try {
      setLoading(true);
      
      // Get the current requirement to store its original description
      const itemData = await requirementsApi.getItem(selectedItemId);
      setOriginalContent(itemData.description || '');
      
      // Fill template with context data - we'll use this in the future for custom templates
      // For now, we'll just use the backend default template
      const response = await requirementsApi.implementRequirement(selectedItemId);
      
      // Store the data and open the dialog
      setImplementationData(response.guidance);
      setImplementationDialogOpen(true);
      
    } catch (err) {
      setError(`Failed to generate implementation guidance: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyImprovedRequirement = async (improvedRequirement) => {
    if (!selectedItemId) return;
    
    try {
      setLoading(true);

      // #572: PUT now requires a `version` precondition — fetch the item's
      // CURRENT version right before applying (not the one from the earlier
      // implement-guidance fetch, which may be stale by the time of applying).
      const current = await requirementsApi.getItem(selectedItemId);
      // Update the requirement with the improved description.
      // #579: same reload / keep-mine affordance as handleApplyRecommendations — a 409
      // between the GET above and this PUT is a real concurrent edit, not an error.
      const res = await saveWithConflictPrompt(selectedItemId, {
        description: improvedRequirement,
        version: current.version
      }, { label: current.display_id || current.title || selectedItemId });

      // Show success message
      setSuccess(res.conflict
        ? 'Not applied — that item had already been changed; reloaded the newer version.'
        : 'Requirement updated successfully');
      
      // Close the dialog
      setImplementationDialogOpen(false);
      
      // Refresh tree if needed
      if (refreshTree) refreshTree();
      
    } catch (err) {
      setError(`Failed to update requirement: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = (model) => {
    setCurrentModel(model);
    // Persist this preference in localStorage
    localStorage.setItem('preferredAiModel', model);
  };

  const handleRefineImplementation = async (message, currentRequirement) => {
    if (!selectedItemId) return;
    
    try {
      setLoading(true);
      
      // Send refinement request to backend
      const response = await requirementsApi.refineImplementation(
        selectedItemId, 
        message,
        currentRequirement
      );
      
      // Update implementation data with refined response
      setImplementationData(response.guidance);
      
      return response; // Return the response for chat history updating
    } catch (err) {
      setError(`Failed to refine implementation: ${err.message}`);
      throw err; // Re-throw for chat history error handling
    } finally {
      setLoading(false);
    }
  };

  return {
    aiDialogOpen,
    aiDialogTitle,
    aiDialogContent,
    aiDialogType,
    aiDialogLoading,
    entitiesDialogOpen,
    entitiesData,
    reviewFeedbackDialogOpen,
    reviewFeedbackData,
    originalContent,
    decomposeDialogOpen,
    decomposeData,
    testCaseDialogOpen,
    testCaseData,
    implementationDialogOpen,
    implementationData,
    currentModel,
    availableModels,
    handleCloseAiDialog,
    handleCloseEntitiesDialog,
    handleCloseReviewFeedbackDialog,
    handleCloseDecomposeDialog,
    handleCloseTestCaseDialog,
    handleCloseImplementationDialog,
    handleReviewWithAI,
    handleCheckConsistency,
    handleDecompose,
    handleGenerateTests,
    handleIdentifyEntities,
    handleApplyRecommendations,
    handleCreateDecomposedRequirements,
    handleCreateTestCases,
    handleApplyEntities,
    handleImplement,
    handleApplyImprovedRequirement,
    handleModelChange,
    handleRefineImplementation
  };
};

export default useAiFunctions;
